import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { teamsAPI } from '../services/api';
import { useTeam } from './TeamContext';
import { todayInZone } from '../utils/timezone';

const StatConfigContext = createContext();

// Default legacy config — matches current hardcoded 7-axis radar (Scoring,
// Creativity, Passing, Defending, Shooting, Availability, Rating). normMax
// values are tuned so an un-configured team renders byte-identical numbers
// to the pre-R142 hardcoded PlayerRadarChart constants.
export const DEFAULT_LEGACY_FIELDS = [
  { key: 'goals', label: 'Goals', source: 'built_in', builtInField: 'goals', dataType: 'integer', category: 'offensive', order: 1 },
  { key: 'assists', label: 'Assists', source: 'built_in', builtInField: 'assists', dataType: 'integer', category: 'offensive', order: 2 },
  { key: 'shots', label: 'Shots', source: 'built_in', builtInField: 'shots', dataType: 'integer', category: 'offensive', order: 3 },
  { key: 'shotsOnTarget', label: 'Shots on Target', source: 'built_in', builtInField: 'shotsOnTarget', dataType: 'integer', category: 'offensive', order: 4 },
  { key: 'passAccuracy', label: 'Pass Accuracy %', source: 'built_in', builtInField: 'passAccuracy', dataType: 'percentage', category: 'technical', order: 5 },
  { key: 'keyPasses', label: 'Key Passes', source: 'built_in', builtInField: 'keyPasses', dataType: 'integer', category: 'technical', order: 6 },
  { key: 'chancesCreated', label: 'Chances Created', source: 'built_in', builtInField: 'chancesCreated', dataType: 'integer', category: 'technical', order: 7 },
  { key: 'dribblesCompleted', label: 'Dribbles', source: 'built_in', builtInField: 'dribblesCompleted', dataType: 'integer', category: 'offensive', order: 8 },
  { key: 'tackles', label: 'Tackles', source: 'built_in', builtInField: 'tackles', dataType: 'integer', category: 'defensive', order: 9 },
  { key: 'interceptions', label: 'Interceptions', source: 'built_in', builtInField: 'interceptions', dataType: 'integer', category: 'defensive', order: 10 },
  { key: 'aerialDuelsWon', label: 'Aerial Duels', source: 'built_in', builtInField: 'aerialDuelsWon', dataType: 'integer', category: 'defensive', order: 11 },
  { key: 'minutesPlayed', label: 'Minutes', source: 'built_in', builtInField: 'minutesPlayed', dataType: 'integer', category: 'other', order: 12 },
  { key: 'distanceCovered', label: 'Distance (km)', source: 'built_in', builtInField: 'distanceCovered', dataType: 'decimal', category: 'other', order: 13 },
  { key: 'yellowCards', label: 'Yellow Cards', source: 'built_in', builtInField: 'yellowCards', dataType: 'integer', category: 'other', higherIsBetter: false, order: 14 },
  { key: 'redCards', label: 'Red Cards', source: 'built_in', builtInField: 'redCards', dataType: 'integer', category: 'other', higherIsBetter: false, order: 15 },
  { key: 'performanceRating', label: 'Rating', source: 'built_in', builtInField: 'performanceRating', dataType: 'rating', category: 'other', order: 16 },
];

export const DEFAULT_LEGACY_RADAR = [
  { key: 'scoring',      label: 'Scoring',      statKeys: ['goals'],                    combine: 'sum_per_match',       normMax: 10 / 3.5, order: 1 },
  { key: 'creativity',   label: 'Creativity',   statKeys: ['assists'],                  combine: 'sum_per_match',       normMax: 2,        order: 2 },
  { key: 'passing',      label: 'Passing',      statKeys: ['passAccuracy'],             combine: 'avg',                 normMax: 100,      order: 3 },
  { key: 'defending',    label: 'Defending',    statKeys: ['tackles', 'interceptions'], combine: 'sum_per_match',       normMax: 25 / 3,   order: 4 },
  { key: 'shooting',     label: 'Shooting',     statKeys: ['shotsOnTarget', 'shots'],   combine: 'per_match_avg_ratio', normMax: 1,        order: 5 },
  { key: 'availability', label: 'Availability', statKeys: ['minutesPlayed'],            combine: 'avg',                 normMax: 90,       order: 6 },
  { key: 'rating',       label: 'Rating',       statKeys: ['performanceRating'],        combine: 'avg',                 normMax: 10,       order: 7, omitWhenEmpty: true },
];

export function StatConfigProvider({ children }) {
  const { activeTeam } = useTeam();
  const [statConfig, setStatConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const latestTeamRef = useRef(null);

  const fetchStatConfig = useCallback(async (teamId) => {
    if (!teamId) return;
    latestTeamRef.current = teamId;
    setLoading(true);
    try {
      const res = await teamsAPI.getStatConfig(teamId);
      if (latestTeamRef.current !== teamId) return; // Stale response — ignore
      const data = res.data?.data;
      setStatConfig(data?.statConfig || null);
      setTemplates(data?.templates || []);
    } catch (err) {
      if (latestTeamRef.current !== teamId) return; // Stale error — ignore
      console.warn('[StatConfig] Failed to load:', err.message);
      setStatConfig(null);
    } finally {
      if (latestTeamRef.current === teamId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTeam?._id) {
      fetchStatConfig(activeTeam._id);
    } else {
      latestTeamRef.current = null;
      setStatConfig(null);
    }
  }, [activeTeam?._id, fetchStatConfig]);

  // Resolved config: team config or legacy defaults. Memoized so consumers'
  // useMemo([stats, fields, radarAxes]) deps are reference-stable across
  // unrelated parent re-renders (PlayerFolder mounts radars per player).
  const fields = useMemo(
    () => statConfig?.fields?.filter(f => f.active !== false)?.sort((a, b) => (a.order || 0) - (b.order || 0)) || DEFAULT_LEGACY_FIELDS,
    [statConfig]
  );
  const radarAxes = useMemo(
    () => statConfig?.radarAxes?.filter(a => a.active !== false)?.sort((a, b) => (a.order || 0) - (b.order || 0)) || DEFAULT_LEGACY_RADAR,
    [statConfig]
  );
  const hasCustomConfig = !!statConfig?.fields?.length;

  // Build default form from fields
  const buildDefaultStatForm = useCallback(() => {
    const form = { matchAgainst: '', date: todayInZone(), position: '', notes: '' };
    for (const field of fields) {
      if (field.source === 'derived') continue; // derived = auto-calculated
      if (field.dataType === 'rating') form[field.key] = '';
      else if (field.key === 'minutesPlayed') form[field.key] = 90;
      else form[field.key] = '';
    }
    return form;
  }, [fields]);

  // Get stat value from entry (handles built-in + custom)
  const getStatValue = useCallback((statEntry, fieldKey) => {
    const field = fields.find(f => f.key === fieldKey);
    if (!field || !statEntry) return null;
    if (field.source === 'built_in' && field.builtInField) {
      const v = statEntry[field.builtInField];
      return v != null ? Number(v) : null;
    }
    const cv = statEntry.customStats?.[fieldKey];
    return cv != null ? Number(cv) : null;
  }, [fields]);

  const refreshStatConfig = useCallback(() => {
    if (activeTeam?._id) return fetchStatConfig(activeTeam._id);
  }, [activeTeam?._id, fetchStatConfig]);

  const contextValue = useMemo(() => ({
    statConfig,
    fields,
    radarAxes,
    templates,
    hasCustomConfig,
    loading,
    buildDefaultStatForm,
    getStatValue,
    refreshStatConfig,
    DEFAULT_LEGACY_FIELDS,
    DEFAULT_LEGACY_RADAR
  }), [statConfig, fields, radarAxes, templates, hasCustomConfig, loading, buildDefaultStatForm, getStatValue, refreshStatConfig]);

  return (
    <StatConfigContext.Provider value={contextValue}>
      {children}
    </StatConfigContext.Provider>
  );
}

export function useStatConfig() {
  return useContext(StatConfigContext);
}
